import { useEffect, useRef } from 'react';
import {
  CHECKOUT_CONTENT_END_ATTRIBUTE,
  hasMeaningfulHeightChange,
  measureCheckoutHeight,
} from '@/utils/checkoutDimensions';
import { notifyCheckoutDimensionsChanged } from '@/utils/notifier';

/**
 * Coalesces the observer storm a single interaction produces (a screen swap
 * fires childList + several box resizes, a `max-height` transition fires one
 * resize per frame) into one measurement. Matched to the 50ms used by the
 * `independent-checkout-v2` original — long enough to collapse a burst, short
 * enough that the host resizes within the same interaction.
 */
const MEASURE_DEBOUNCE_MS = 50;

/**
 * Reports the checkout's content height to the embedding SDK whenever it
 * changes, so the host can size the iframe to it. Attach the returned ref to the
 * column's scroll viewport; the height is then measured from that element's
 * content top down to the bottom of the Footer (see
 * {@link measureCheckoutHeight}).
 *
 * Wired per view rather than globally: only templates that can outgrow the
 * iframe need it (EMBEDDED_VIEW and EMBEDDED_PREFERRED_PAYMENT_METHOD), and the
 * ref has to land on that view's scroll container.
 *
 * Height moves for reasons no single React state can be watched for — an APM
 * form replacing another, a sub-screen (new card / other payment methods /
 * saved cards) swapping into the column, a validation error opening a row, a
 * payment-method logo finishing decode — so this observes the DOM instead:
 *
 *   - `ResizeObserver` on the container, its direct children AND the measured
 *     content-end box. The container alone is not enough: it is a fixed-height
 *     (`h-full`) viewport, so its own box never changes when the content inside
 *     it grows.
 *   - `MutationObserver` on the subtree for content that is added or removed
 *     without resizing a child that already existed — and to re-sync the
 *     ResizeObserver onto children that appeared since the last pass.
 */
export const useCheckoutDimensionsReporter = <T extends HTMLElement = HTMLDivElement>() => {
  const containerRef = useRef<T | null>(null);
  const lastReportedHeightRef = useRef<number>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver !== 'function' || typeof MutationObserver !== 'function') return;

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;

    const report = () => {
      const height = measureCheckoutHeight(container);
      if (!hasMeaningfulHeightChange(lastReportedHeightRef.current, height)) return;
      lastReportedHeightRef.current = height;
      notifyCheckoutDimensionsChanged(height);
    };

    const scheduleReport = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(report, MEASURE_DEBOUNCE_MS);
    };

    const resizeObserver = new ResizeObserver(scheduleReport);
    const observeBoxes = () => {
      resizeObserver.disconnect();
      // The container carries the viewport-height changes (rotation, keyboard),
      // its children the content-height ones.
      resizeObserver.observe(container);
      for (const child of Array.from(container.children)) {
        resizeObserver.observe(child);
      }
      // The box we actually measure to is nested deeper, and the wrapper between
      // it and the container is stretched (`flex-1`) — so that wrapper keeps its
      // size while the content inside grows, and observing only direct children
      // would miss every form-height change.
      const contentEnd = container.querySelector(`[${CHECKOUT_CONTENT_END_ATTRIBUTE}]`);
      if (contentEnd) resizeObserver.observe(contentEnd);
    };

    const mutationObserver = new MutationObserver(() => {
      observeBoxes();
      scheduleReport();
    });

    observeBoxes();
    mutationObserver.observe(container, { childList: true, subtree: true });
    // Baseline for the host, before any interaction: the ResizeObserver's own
    // first callback is debounced, and until one lands the host is still sizing
    // the iframe off its default.
    report();

    return () => {
      clearTimeout(debounceTimer);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return containerRef;
};
